import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, WabaAccount, WabaDispatchUsage } from '../services/api';
import { Phone, ChevronDown, Home, Star, Users, ArrowLeft, AlertTriangle, Gauge, Infinity } from 'lucide-react';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { qualityRatingColors, getQualityRatingLabel } from '../utils/statusColors';

// Importar as abas
import Campaigns from '../components/waba/Campaigns';
import CampaignTemplatesTab from '../components/waba/CampaignTemplatesTab';
import Templates from '../components/waba/Templates';
import SimpleDashboard from '../components/waba/SimpleDashboard';

type TabType = 'campaign-templates' | 'standalone-campaigns' | 'waba-templates' | 'dashboard';

export default function WabaEnvironment() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken, user, logout, isAuthenticated, isLoading: authLoading } = useAuth();

  const [currentAccount, setCurrentAccount] = useState<WabaAccount | null>(null);
  const [allAccounts, setAllAccounts] = useState<WabaAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('campaign-templates');
  const [dispatchUsage, setDispatchUsage] = useState<WabaDispatchUsage | null>(null);

  // Check if user is admin (to show back to dashboard button)
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  // Helper function para cores do quality rating (text-only, usando tokens)
  const getQualityRatingTextColor = (rating?: string) => {
    const key = rating?.toLowerCase() || 'default';
    const config = qualityRatingColors[key] || qualityRatingColors['default'];
    return config.text;
  };

  // Verificar se a qualidade está bloqueada (LOW/RED)
  const isQualityBlocked = (rating?: string) => {
    const normalizedRating = rating?.toLowerCase();
    return normalizedRating === 'low' || normalizedRating === 'red';
  };

  // Redirecionar para login se não estiver autenticado
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Buscar todas as contas WABA
  useEffect(() => {
    const fetchAccounts = async () => {
      if (!accessToken || !id) return;

      try {
        setIsLoading(true);
        const response = await apiClient.getWabaAccounts(accessToken);

        if (response.success) {
          const accounts = response.data.wabaAccounts || [];
          setAllAccounts(accounts);

          // Encontrar a conta atual
          const account = accounts.find((acc) => acc.id === id);
          if (account) {
            setCurrentAccount(account);
          } else {
            setError('Conta WABA não encontrada');
            setTimeout(() => navigate('/waba'), 2000);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar contas');
      } finally {
        setIsLoading(false);
      }
    };

    if (!authLoading && accessToken) {
      fetchAccounts();
    }
  }, [accessToken, authLoading, id, navigate]);

  // Fetch dispatch usage when account changes
  useEffect(() => {
    const fetchDispatchUsage = async () => {
      if (!accessToken || !id) return;

      try {
        const response = await apiClient.getWabaDispatchUsage(id, accessToken);
        if (response.success) {
          setDispatchUsage(response.data);
        }
      } catch (err) {
        console.error('Erro ao buscar uso de disparos:', err);
      }
    };

    if (!authLoading && accessToken && id) {
      fetchDispatchUsage();
    }
  }, [accessToken, authLoading, id]);

  const handleAccountChange = (accountId: string) => {
    setShowAccountDropdown(false);
    navigate(`/waba/${accountId}/environment`);
  };

  if (isLoading || authLoading) {
    return (
      <LoadingSpinner fullPage text="Carregando..." />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-bg-1 flex items-center justify-center">
        <div className="text-center">
          <p className="text-danger mb-4">{error}</p>
          <Link to="/waba" className="text-accent-1 hover:underline">
            Voltar para contas WABA
          </Link>
        </div>
      </div>
    );
  }

  if (!currentAccount) {
    return null;
  }

  const tabs = [
    { id: 'campaign-templates', label: 'Campanhas de Disparo', component: CampaignTemplatesTab },
    { id: 'standalone-campaigns', label: 'Campanhas Avulsas', component: Campaigns },
    { id: 'waba-templates', label: 'Templates WhatsApp', component: Templates },
    { id: 'dashboard', label: 'Resumo', component: SimpleDashboard },
  ] as const;

  const ActiveTabComponent = tabs.find((tab) => tab.id === activeTab)?.component || CampaignTemplatesTab;

  return (
    <div className="min-h-screen bg-bg-1">
      {/* Header */}
      <header className="bg-bg-2 border-b border-bg-3 shadow-sm sticky top-0 z-50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            {/* Voltar + Seletor de Ambiente */}
            <div className="flex items-center gap-4">
              {/* Back to Dashboard - only for admins */}
              {isAdmin && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/admin/dashboard');
                  }}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-bg-3 rounded-md transition-colors text-text-2 hover:text-accent-1"
                  title="Voltar ao Dashboard Admin"
                >
                  <ArrowLeft size={18} />
                  <span className="text-sm font-medium">Dashboard</span>
                </button>
              )}

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/waba');
                }}
                className="flex items-center gap-2 px-3 py-2 hover:bg-bg-3 rounded-md transition-colors text-text-2 hover:text-accent-1"
                title="Voltar para ambientes"
              >
                <Home size={18} />
                <span className="text-sm font-medium">Ambientes</span>
              </button>

              {/* Dropdown de Ambientes */}
              <div className="relative">
                <button
                  onClick={() => setShowAccountDropdown(!showAccountDropdown)}
                  className="flex items-center gap-4 px-4 py-2.5 bg-bg-3 hover:bg-bg-3/80 rounded-md transition-colors"
                >
                  <Phone size={20} className="text-accent-1 flex-shrink-0" />

                  {/* Informações em linha horizontal */}
                  <div className="flex items-center gap-6">
                    {/* Telefone */}
                    <div className="flex flex-col">
                      <span className="text-[10px] text-text-3 uppercase tracking-wide">Número</span>
                      <span className="text-sm font-semibold text-text-1">{currentAccount.displayPhoneNumber}</span>
                    </div>

                    {/* Business Name */}
                    {currentAccount.businessName && (
                      <div className="flex flex-col max-w-[200px]">
                        <span className="text-[10px] text-text-3 uppercase tracking-wide">Empresa</span>
                        <span className="text-sm font-medium text-text-1 truncate">{currentAccount.businessName}</span>
                      </div>
                    )}

                    {/* Quality Rating */}
                    {currentAccount.qualityRating && (
                      <div className="flex flex-col">
                        <span className="text-[10px] text-text-3 uppercase tracking-wide">Qualidade</span>
                        <div className="flex items-center gap-1.5">
                          <Star size={14} className={getQualityRatingTextColor(currentAccount.qualityRating)} />
                          <span className={`text-sm font-semibold ${getQualityRatingTextColor(currentAccount.qualityRating)}`}>
                            {getQualityRatingLabel(currentAccount.qualityRating)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Limite de Disparo (24h) - substitui Limite Diário quando configurado */}
                    {dispatchUsage && !dispatchUsage.isUnlimited ? (
                      <div className="flex flex-col">
                        <span className="text-[10px] text-text-3 uppercase tracking-wide">Disparo (24h)</span>
                        <div className="flex items-center gap-1.5">
                          <Gauge size={14} className={
                            dispatchUsage.utilizationPercentage > 90 ? 'text-danger' :
                            dispatchUsage.utilizationPercentage > 70 ? 'text-warning' : 'text-success'
                          } />
                          <span className={`text-sm font-semibold ${
                            dispatchUsage.utilizationPercentage > 90 ? 'text-danger' :
                            dispatchUsage.utilizationPercentage > 70 ? 'text-warning' : 'text-text-1'
                          }`}>
                            {dispatchUsage.usedIn24h.toLocaleString()}/{dispatchUsage.dailyDispatchLimit.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ) : (
                      /* Limite Diário - só mostra quando não há limite de disparo configurado */
                      currentAccount.maxDailyConversations && (
                        <div className="flex flex-col">
                          <span className="text-[10px] text-text-3 uppercase tracking-wide">Limite Diário</span>
                          <div className="flex items-center gap-1.5">
                            <Users size={14} className="text-accent-1" />
                            <span className="text-sm font-semibold text-text-1">
                              {currentAccount.maxDailyConversations.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  <ChevronDown size={16} className="text-text-3 flex-shrink-0 ml-2" />
                </button>

                {/* Dropdown Menu */}
                {showAccountDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowAccountDropdown(false)}
                    />
                    <div className="absolute top-full left-0 mt-2 w-96 bg-bg-1 border border-border-1 rounded-md shadow-xl z-20">
                      <div className="p-2">
                        <p className="text-xs text-text-3 px-3 py-2">Trocar de ambiente</p>
                        {allAccounts.map((account) => (
                          <button
                            key={account.id}
                            onClick={() => handleAccountChange(account.id)}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                              account.id === currentAccount.id
                                ? 'bg-info/10 text-accent-1'
                                : 'hover:bg-bg-3 text-text-1'
                            }`}
                          >
                            <Phone size={16} className="flex-shrink-0" />
                            <div className="flex-1 text-left min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium">
                                  {account.displayPhoneNumber}
                                </p>
                                {account.qualityRating && (
                                  <div className="flex items-center gap-0.5">
                                    <Star size={10} className={getQualityRatingTextColor(account.qualityRating)} />
                                    <span className={`text-xs ${getQualityRatingTextColor(account.qualityRating)}`}>
                                      {getQualityRatingLabel(account.qualityRating)}
                                    </span>
                                  </div>
                                )}
                              </div>
                              {account.businessName && (
                                <p className="text-xs text-text-2 truncate">
                                  {account.businessName}
                                </p>
                              )}
                              <div className="flex items-center gap-2 text-xs text-text-3">
                                <span>{account.isActive ? 'Ativa' : 'Inativa'}</span>
                                {/* Mostra limite de disparo se configurado, senão limite diário */}
                                {account.dailyDispatchLimit && account.dailyDispatchLimit > 0 ? (
                                  <>
                                    <span>•</span>
                                    <span>{account.dailyDispatchLimit.toLocaleString()}/dia (24h)</span>
                                  </>
                                ) : account.maxDailyConversations ? (
                                  <>
                                    <span>•</span>
                                    <span>{account.maxDailyConversations.toLocaleString()}/dia</span>
                                  </>
                                ) : null}
                              </div>
                            </div>
                            {account.id === currentAccount.id && (
                              <div className="w-2 h-2 bg-accent-1 rounded-full" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* User Info + Logout */}
            <div className="flex items-center gap-3">
              {user && (
                <div className="text-right">
                  <p className="text-sm text-text-1 font-medium">{user.name}</p>
                  <p className="text-xs text-text-3">{user.email}</p>
                </div>
              )}
              <button
                onClick={() => {
                  // Limpar localStorage
                  localStorage.removeItem('user');
                  localStorage.removeItem('accessToken');
                  localStorage.removeItem('refreshToken');

                  // Chamar função de logout do contexto
                  logout();

                  // Redirecionar para login
                  navigate('/login');
                }}
                className="flex items-center gap-2 px-3 py-2 bg-danger hover:bg-danger/90 text-white rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-danger focus:ring-offset-2 focus:ring-offset-bg-2"
                title="Sair"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                Sair
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-4 flex gap-1 border-b border-bg-3">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                  activeTab === tab.id
                    ? 'text-accent-1'
                    : 'text-text-2 hover:text-text-1'
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent-1" />
                )}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Banner de Conta Banida/Bloqueada */}
        {currentAccount?.accountStatus?.toUpperCase() === 'BANNED' && (
          <div className="bg-danger/10 border border-danger rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="text-danger flex-shrink-0" size={24} />
            <div>
              <p className="font-semibold text-danger">
                Conta Banida — Disparos Bloqueados
              </p>
              <p className="text-sm text-danger/80">
                Esta conta foi banida pela Meta/WhatsApp. Não é possível enviar mensagens.
                Contate o suporte para resolver.
              </p>
            </div>
          </div>
        )}

        {currentAccount?.accountStatus?.toUpperCase() === 'BLOCKED' && (
          <div className="bg-danger/10 border border-danger rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="text-danger flex-shrink-0" size={24} />
            <div>
              <p className="font-semibold text-danger">
                Conta Bloqueada — Disparos Bloqueados
              </p>
              <p className="text-sm text-danger/80">
                Esta conta está com restrições. Verifique no Business Manager da Meta.
              </p>
            </div>
          </div>
        )}

        {currentAccount?.accountStatus?.toUpperCase() === 'LIMITED' && (
          <div className="bg-warning/10 border border-warning rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="text-warning flex-shrink-0" size={24} />
            <div>
              <p className="font-semibold text-warning">
                Conta com Limite Reduzido
              </p>
              <p className="text-sm text-warning/80">
                O número está com limite de envio reduzido pela Meta.
              </p>
            </div>
          </div>
        )}

        {/* Banner de Qualidade Baixa */}
        {isQualityBlocked(currentAccount?.qualityRating) && (
          <div className="bg-danger/10 border border-danger rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertTriangle className="text-danger flex-shrink-0" size={24} />
            <div>
              <p className="font-semibold text-danger">
                Conta com Qualidade Baixa - Disparos Bloqueados
              </p>
              <p className="text-sm text-danger/80">
                Esta conta está temporariamente impedida de realizar disparos para evitar banimento.
                Aguarde a qualidade melhorar para continuar.
              </p>
            </div>
          </div>
        )}

        <ActiveTabComponent wabaAccountId={id!} qualityRating={currentAccount?.qualityRating} {...({ accountStatus: currentAccount?.accountStatus } as any)} />
      </main>
    </div>
  );
}
